import React, { useCallback, useMemo } from "react";

import { MainTeaser, TeaserProps } from "./MainTeaser";
import { Spacer } from "shared/layout";
import { EventTravelLegType, IDepartureArrival } from "Bands/types";
import {
  BoldText,
  CenteredText,
  FlexContainer,
  Icon,
  MutedDarkText,
  MutedText,
  SmallText,
  TinyText,
} from "shared/styles";
import {
  formatDateTime,
  formatTime,
  getRegionalSettingsOptions,
  getTravelTime,
} from "shared/utils";
import { ArrowRight, Info } from "shared/assets/icons";
import { colors } from "shared/styles/constants";
import {
  StyledPersonalTicket,
  StyledTravelLegInfo,
  StyledTravelLegTeaser,
} from "./styles";
import { getTravelLegTitle, useBandRegionalSettings } from "Bands/utils";
import { copy } from "shared/assets";
import { FancyAnchorLink } from "../Link";
import { IStatus } from "shared/types";

const { timezoneToUtc } = getRegionalSettingsOptions();

const passengerCopy = copy.travelBlock.passenger;

type Props = EventTravelLegType &
  TeaserProps & {
    index: number;
    showPersonalTicket?: boolean;
  };

export const TravelLegTeaser: React.FC<Props> = ({
  index,
  departure,
  arrival,
  info,
  actions,
  onClick,
  disabled,
  showPersonalTicket,
  personalBookingTicket,
  ...rest
}) => {
  const { title, subTitle, prefix } = getTravelLegTitle(rest, index);

  const travelInfo = useMemo(
    () => (
      <Spacer size="xxs">
        <TinyText as={MutedDarkText}>{prefix}</TinyText>
        <BoldText>{title}</BoldText>
        <TinyText as={MutedText}>{subTitle}</TinyText>
      </Spacer>
    ),
    [prefix, subTitle, title]
  );

  const { dateFormat, timeFormat } = useBandRegionalSettings();

  const renderDepartureArrival = useCallback(
    ({ location, date, time, timeZone }: Partial<IDepartureArrival>) => {
      if (!date || !time) return null;

      const formattedDate = formatDateTime(date, {
        dateOnly: true,
        dateFormat,
      });
      const formattedTime = formatTime({ time, timeFormat });

      return (
        <Spacer size="xxs" align="center">
          <TinyText as={MutedText}>
            <CenteredText>{location?.data.attributes.location}</CenteredText>
          </TinyText>
          <TinyText as={MutedDarkText}>
            <CenteredText>{formattedDate}</CenteredText>
          </TinyText>
          <BoldText as={CenteredText}>{formattedTime}</BoldText>
          {timeZone && (
            <TinyText as={MutedDarkText}>
              <CenteredText>{timezoneToUtc(timeZone, null, true)}</CenteredText>
            </TinyText>
          )}
        </Spacer>
      );
    },
    [dateFormat, timeFormat]
  );

  const travelTime = useMemo(() => {
    if (!departure || !arrival) return;

    return (
      <Spacer size="xxs" align="center" justifyContent="center">
        <CenteredText>
          <TinyText as={MutedText}>
            {getTravelTime(departure, arrival)}
          </TinyText>
        </CenteredText>
        <Icon as={ArrowRight} color={colors.grey4} />
      </Spacer>
    );
  }, [arrival, departure]);

  const personalTicket = useMemo(() => {
    const { bookingReference, files } = personalBookingTicket || {};

    if (!bookingReference && !files?.length) return;

    return (
      <StyledPersonalTicket>
        <Spacer size="xs">
          <BoldText>{passengerCopy.messages.yourInfo}</BoldText>
          {bookingReference && (
            <SmallText>
              <BoldText>{passengerCopy.fields.bookingReference}: </BoldText>
              {personalBookingTicket?.bookingReference}
            </SmallText>
          )}
          {files?.[0]?.source && (
            <SmallText>
              <FancyAnchorLink
                href={files[0].source}
                status={IStatus.warning}
                target="_blank"
              >
                {passengerCopy.fields.ticket}
              </FancyAnchorLink>
            </SmallText>
          )}
        </Spacer>
      </StyledPersonalTicket>
    );
  }, [personalBookingTicket]);

  return (
    <MainTeaser
      actions={actions}
      data-testid="travel_leg_teaser"
      onClick={onClick}
      disabled={disabled}
    >
      <Spacer size="m">
        <StyledTravelLegTeaser>
          <StyledTravelLegInfo style={{ gridColumn: "span 2" }}>
            {travelInfo}
          </StyledTravelLegInfo>
          <div style={{ gridColumn: "span 2" }}>
            {departure && renderDepartureArrival(departure)}
          </div>
          {travelTime}
          <div style={{ gridColumn: "span 2" }}>
            {arrival && renderDepartureArrival(arrival)}
          </div>
          {showPersonalTicket && personalBookingTicket && (
            <div style={{ gridColumn: "span 2" }}>{personalTicket}</div>
          )}
        </StyledTravelLegTeaser>
        {info && (
          <FlexContainer>
            <Icon as={Info} color={colors.grey4} />
            <SmallText
              as={MutedText}
              dangerouslySetInnerHTML={{ __html: info }}
            />
          </FlexContainer>
        )}
      </Spacer>
    </MainTeaser>
  );
};
